<template>
  <dropdown title="Jobs" class="ifp-filter-jobs" @close="selectedJobName=null">
    <base-ifp-filter :label="jobsLabel" clearable @clear-clicked="handleClearClick()">
      <template #value-display>
        <template v-if="hasOnlyOneJob">
          <avatar-gallery 
            :people="getAppointeesForJobName(internalSelectedJobAppointeeGroupings[0].jobName)" 
            :max-num-avatars="3" 
            :size="18" 
            stack-tightly
            suppress-avatar-tooltip
            suppress-remaining-people-tooltip
          />
        </template>
        <template v-else-if="internalSelectedJobAppointeeGroupings.length > 1">
          {{ internalSelectedJobAppointeeGroupings.length }} selected
        </template>
      </template>
    </base-ifp-filter>
    <template #content>
      <div>
        <ul>
          <li v-for="job in displayableJobOptions" :key="job.id" @click="selectedJobName=job.name">
            <!-- Beside the job: -8 lines the first appointee up with it, 14 clears the jobs list by 1px -->
            <dropdown :key="job.id" :title="job.name" placement="right-start" :offset="[-8, 14]">
              <div
                :class="[
                  { 'selected' : selectedJobName === job.name }, 
                  { 'has-appointees' : hasAppointeesForJobName(job.name) }
                ]" class="ifp-filter-jobs__label">
                <span>
                  {{ job.name }}&hellip;&nbsp;
                  <avatar-gallery 
                    v-if="hasAppointeesForJobName(job.name)" 
                    :people="getAppointeesForJobName(job.name)" 
                    :max-num-avatars="3" 
                    :size="18" 
                    stack-tightly
                    suppress-avatar-tooltip
                    suppress-remaining-people-tooltip
                  />
                </span>
                <i v-if="selectedJobName === job.name" class="ddi ddi-arrow-right" />
              </div>
              <template #content>
                <div>
                  <ul>
                    <li v-for="appointee in curatedAppointeeOptions" :key="appointee.id" @click="handleJobAppointeeSelection(job.name, appointee.id)">
                      <avatar :person="appointee" :size="20"/>
                      <span :class="{'selected' : isAppointeeSelected(job.name, appointee.id)}">{{ appointee.name }}</span>
                    </li>
                  </ul>
                </div>
              </template>
            </dropdown>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'

/* Mixins */

/* Components */

/* Helpers */
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import SortAssistant from '@/helpers/sort-assistant'
import AssigneeAssistant from '@/helpers/assignee-assistant'

/* Data types */
import { IItemForSelection, IPersonWithEmail } from '@/api/types/response.types'
import { IItemsInputJobAppointeeGrouping } from '@/api/types/request.types'
import avatarGallery from '@/components/global/avatar-gallery.vue'
import Avatar from '@/components/global/avatar.vue'

/* API */

export default defineComponent({
  name: 'IfpFilterJobs',
  components: { avatarGallery, Avatar },

  props: {
    jobOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
    appointeeOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },
    selectedJobAppointeeGroupingsString: {
      type: String,
      default: '',
    },
  },

  emits: ['selected-job-appointee-groupings-string-updated'],

  data() {
    return {
      debounceEmitSelectedJobAppointeeGroupings: null as any,
      selectedJobName: null as any,
      internalSelectedJobAppointeeGroupings: [] as IItemsInputJobAppointeeGrouping[],
      emitDebounceTimeInMS: 500,
    }
  },

  computed: {
    // If showing multiple workflows, job names can overlap. Let's extract
    // distinct names (using the first instance if multiple).
    displayableJobOptions(): IItemForSelection[] {
      return this.jobOptions.filter(
        (j, i, arr) =>
          arr.findIndex(
            t => t.name.toLowerCase().trim() === j.name.toLowerCase().trim(),
          ) === i,
      )
    },
    curatedAppointeeOptions(): IPersonWithEmail[] {
      return AssigneeAssistant.getFullAssigneeOptions(
        this.appointeeOptions,
        this.$permissions,
      )
    },
    jobsLabel(): string {
      if (this.hasOnlyOneJob) {
        /* Doing this so that the non-lower-cased job name is surfaced.
           Also, there is an edge-case where, if the job name has been changed
           by the user, then we don't get any matches because we are storing the
           filter by the job name. In those cases, we'll always be returning no record
           matches. But, for the purposes of the label, we will just show "Jobs" and the 
           requisite appointee avatars. Not ideal, but it's an edge case (and it doesn't
           break the app). */
        const loneJobName = this.internalSelectedJobAppointeeGroupings[0]
          .jobName

        const matchingJobOption = this.jobOptions.find(
          jo => jo.name.toLowerCase().trim() === loneJobName,
        )

        if (matchingJobOption) {
          return matchingJobOption.name
        }
      }
      return 'Jobs'
    },
    hasOnlyOneJob(): boolean {
      return this.internalSelectedJobAppointeeGroupings.length === 1
    },
  },

  watch: {
    selectedJobAppointeeGroupingsString: {
      immediate: true,
      handler(val) {
        this.internalSelectedJobAppointeeGroupings = ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
          val,
        )
      },
    },
  },

  created() {
    this.debounceEmitSelectedJobAppointeeGroupings = debounce(
      this.emitSelectedJobAppointeeGroupingsString,
      this.emitDebounceTimeInMS,
    )
  },

  methods: {
    updateSelectedJobAppointeeGroupingsString(
      jagString: string,
      emitImmediately?: boolean,
    ) {
      if (emitImmediately) {
        this.emitSelectedJobAppointeeGroupingsString(jagString)
      } else {
        this.debounceEmitSelectedJobAppointeeGroupings(jagString)
      }
    },
    emitSelectedJobAppointeeGroupingsString(jagString: string) {
      this.$emit('selected-job-appointee-groupings-string-updated', jagString)
    },
    handleClearClick() {
      this.updateSelectedJobAppointeeGroupingsString('', true)
    },
    sortGroupingsDataChronologically(
      groupings: IItemsInputJobAppointeeGrouping[],
    ): IItemsInputJobAppointeeGrouping[] {
      return groupings.map(
        g =>
          ({
            jobName: g.jobName,
            listAppointeeIDs: SortAssistant.sortChronologically(
              g.listAppointeeIDs,
            ),
          } as IItemsInputJobAppointeeGrouping),
      )
    },
    hasAppointeesForJobName(jobName: string): boolean {
      return this.getAppointeesForJobName(jobName).length > 0
    },
    getAppointeesForJobName(jobName: string): IPersonWithEmail[] {
      let result = [] as IPersonWithEmail[]
      const relevantJobAppointeeGrouping = this.getJobAppointeeGroupingForJobName(
        jobName,
      )
      if (relevantJobAppointeeGrouping) {
        result = this.curatedAppointeeOptions.filter(ao =>
          relevantJobAppointeeGrouping.listAppointeeIDs.includes(ao.id),
        )
      }
      return result
    },
    getJobAppointeeGroupingForJobName(
      jobName: string,
    ): IItemsInputJobAppointeeGrouping {
      return this.internalSelectedJobAppointeeGroupings.find(
        jag =>
          jag.jobName.toLowerCase().trim() === jobName.toLowerCase().trim(),
      )
    },
    isAppointeeSelected(jobName: string, appointeeID: number): boolean {
      return this.getAppointeesForJobName(jobName)
        .map(a => a.id)
        .includes(appointeeID)
    },
    handleJobAppointeeSelection(jobName: string, appointeeID: number) {
      const isSelected = this.isAppointeeSelected(jobName, appointeeID)

      if (isSelected) {
        // Deselect...
        const relevantJobAppointeeGrouping = this.getJobAppointeeGroupingForJobName(
          jobName,
        )
        relevantJobAppointeeGrouping.listAppointeeIDs = relevantJobAppointeeGrouping.listAppointeeIDs.filter(
          a => a !== appointeeID,
        )
      } else {
        // Select...
        const relevantJobAppointeeGrouping = this.getJobAppointeeGroupingForJobName(
          jobName,
        )

        if (relevantJobAppointeeGrouping) {
          relevantJobAppointeeGrouping.listAppointeeIDs.push(appointeeID)
        } else {
          this.internalSelectedJobAppointeeGroupings.push({
            jobName: jobName.toLowerCase().trim(),
            listAppointeeIDs: [appointeeID],
          } as IItemsInputJobAppointeeGrouping)
        }
      }

      // Re-sort the IDs for cleanliness...
      this.internalSelectedJobAppointeeGroupings = this.sortGroupingsDataChronologically(
        this.internalSelectedJobAppointeeGroupings,
      )

      this.updateSelectedJobAppointeeGroupingsString(
        ItemFilterAssistant.convertJobAppointeeGroupingsToStringRepresentation(
          this.internalSelectedJobAppointeeGroupings,
        ),
      )
    },
  },
})
</script>
<style lang="scss">
.ifp-filter-jobs {
  &__label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: -1 * $dropdown-item-li-vertical-padding -1 * $dropdown-item-li-horizontal-padding;
    padding: $dropdown-item-li-vertical-padding
      $dropdown-item-li-horizontal-padding;
    border-radius: 4px;

    span {
      @include single-line-ellipsis;
    }

    .avatar-gallery {
      display: inline;
    }

    &.has-appointees {
      font-weight: $bold;
    }

    &.selected {
      background-color: var(--color-background-blue-15);
    }
  }
  /* The popper with the job names */
  > .dropdown__popper {
    max-width: 250px;
    min-width: 250px;
    /* Need overflow: visible to allow appointees dropdown to appear since it's inside this jobs dropdown container.
       Also overriding max-height so jobs dropdown expands as needed (otherwise jobs will overflow if there's enough
       to go beyond the default max-height set in dropdown.vue) */
    overflow: visible;
    max-height: 100vh;
    .dropdown {
      width: 100%;
    }
    /* The second popper with the potential job appointees */
    .dropdown__popper {
      max-height: 300px;
    }
  }

  > .action-sheet {
    .dropdown {
      width: 100%;
    }
  }
}
</style>
